import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import * as actions from "../actions";
import { fetchApiProduct } from "../actions/apiProduct";
import ErrorMessage from "../components/errorMessage/errorMessage";
import { FRONTEND_ORIGIN_URL_NO_SLASH } from "../config";
import ChatBot from "../components/chatBot/chatBot";
import RedocTryoutHOC from "../components/redoc-tryout/redoc-tryoutHOC";
import Breadcrumb from "../components/breadcrumb/breadcrumb";
import SectionStart from "../components/sectionStart/sectionStart";
import withTracker from "./withtracker";
import AuthorizationHOC from "./AuthorizationHOC";
import { ALL_USERS } from "../constants/userGroups";
import MetaData from "../components/meta/meta";
import { getRouteUrl } from "../routers";
import { ROUTE_PRODUCT_DOCUMENTATION } from "../routers/content/product";
import { parseDocumentations } from "../constants/parse/documentation";
import { parseApiProduct } from "../constants/parse/product";
import _ from "lodash";
import { parseSwaggerFromBase64 } from "../utils/swaggerUtils";
import PagePreloader from "../components/pagePreloader/pagePreloader";
import ChangeLog from "../components/changeLog/changeLog";
import "../components/changeLog/changeLog.css";
import Heading from "../components/heading/heading";
import v4 from "uuid";
import APIProductService from "../services/APIProductService";
import { getUserIdFromState } from "../mappers/backendMappers";
import {
  normalizeAlias,
  findProductByAlias,
  findProductByFamilyAndVersion,
  resolveLatestActiveVersionForFamily,
  productLink,
} from "../utils/resolveProductFamily";

function ApiDocumentationHOC(props) {
  const {
    fetchApiDocumentationByAlias,
    apiDocumentations,
    match,
    errorMessage,
    breadcrumb,
    fetchApiProduct,
    location,
    currentProduct,
  } = props;

  // Handle three route patterns:
  // 1. /documentation/:combinedParam (single parameter - could be UUID--identifier OR clean alias)
  // 2. /documentation/:productId/:apiName (traditional two-parameter route)
  let productId = match.params.productId;
  let apiName = match.params.apiName;
  const combinedParam = match.params.combinedParam;
  let useApiAlias = false; // Flag to determine which loading method to use

  // If we have a combined parameter, check what format it is
  if (combinedParam && !productId && !apiName) {
    const decoded = decodeURIComponent(combinedParam);

    // Check if it's the UUID--identifier format
    if (decoded.includes("--")) {
      const parts = decoded.split("--");
      if (parts.length === 2) {
        productId = parts[0];
        apiName = parts[1];
      }
    } else {
      // It's a clean alias - we'll use the new API endpoint
      useApiAlias = true;
      // Reconstruct the full alias path that the backend expects
      apiName = `/documentation/${decoded}`;
    }
  }

  const { title, botEnabled, botUrl, product } = getApiDocumentation(apiName);

  const [swaggerSpec, setSwaggerSpec] = useState(null);
  const [apiEndpointData, setApiEndpointData] = useState(null);
  // Product this documentation belongs to - the try-out's "Select Client" subscribes an app to it.
  const [productUuid, setProductUuid] = useState(null);
  // Product loaded for a /documentation/<api alias> link. The redux lookup below only works for
  // /documentation/<product>/<api> routes, so without this the breadcrumb stayed empty.
  const [aliasProduct, setAliasProduct] = useState(null);
  // Error raised while loading the product/endpoint for this page. Without it a failed
  // fetch left the full-screen preloader up forever (the redux errorMessage below is only
  // set by the legacy apiDocumentation actions, which this page does not dispatch).
  const [loadError, setLoadError] = useState(null);
  // Tracks the fetch already issued for the current route + user so the effect does not
  // fire the same request again when the CSRF token arrives after mount. A failed attempt
  // is allowed to retry.
  const fetchStateRef = useRef({ key: null, status: null });

  // changeLog is already parsed onto currentProduct by parseApiProduct - it's embedded
  // on the product document itself, not a separate fetch. See devportal-manager-cms-feature memory.
  const parsedChangeLog = currentProduct?.changeLog || [];

  // Product shown in the breadcrumb ("Products / <product> / Documentation / <api>"), linked the
  // same version-less way as the product cards.
  const crumbProduct = currentProduct || product || aliasProduct;
  const crumbProductHref = crumbProduct
    ? productLink(crumbProduct.alias, crumbProduct.deprecated) ||
      crumbProduct.alias
    : null;

  // Use title from route state if available or from fetched endpoint data
  const displayTitle =
    location?.state?.apiTitle || apiEndpointData?.title || title;

  useEffect(() => {
    // Check if swagger data was passed via route state
    if (location?.state?.swagger) {
      const swaggerJson = parseSwaggerFromBase64(location.state.swagger);
      if (swaggerJson) {
        setSwaggerSpec(swaggerJson);
        return;
      }
    }

    const fetchKey = `${useApiAlias}|${productId || ""}|${apiName || ""}|${
      props.userId || ""
    }`;
    const previous = fetchStateRef.current;
    if (previous.key === fetchKey && previous.status !== "failed") {
      return; // same request already in flight or completed
    }
    const markStarted = () => {
      fetchStateRef.current = { key: fetchKey, status: "pending" };
      setLoadError(null);
    };
    const markDone = () => {
      fetchStateRef.current = { key: fetchKey, status: "done" };
    };
    const markFailed = (message) => {
      fetchStateRef.current = { key: fetchKey, status: "failed" };
      setLoadError(message);
    };
    const NOT_FOUND_MESSAGE =
      "The requested API documentation could not be found.";
    const LOAD_FAILED_MESSAGE =
      "The API documentation could not be loaded. Please try again later.";

    // Handle clean alias route (new approach)
    if (useApiAlias && apiName) {
      const fetchProductByAlias = async () => {
        markStarted();
        try {
          const userId = props.userId || "00000000-0000-0000-0000-000000000000";
          const csrfToken = props.csrfToken || "";

          // Use the new backend endpoint that accepts API endpoint alias
          const productData = await APIProductService.getProductByApiAlias(
            apiName,
            userId,
            csrfToken
          );

          if (productData && productData.mapped) {
            setAliasProduct(productData.mapped);
            // Keep Redux in sync
            if (productData.mapped.uuid) {
              setProductUuid(productData.mapped.uuid);
              fetchApiProduct({ apiProductId: productData.mapped.uuid });
            }

            // Find the API endpoint in the product
            const api = APIProductService.findAPI(apiName);

            if (api) {
              setApiEndpointData({
                title: api.apiName || api.ApiName,
                alias: api.urlAlias || api.UrlAlias || api.alias || null,
                swagger: api.swagger || api.Swagger,
                documentation: api.redocDocumentation || api.RedocDocumentation,
                version: api.apiVersion || api.ApiVersion,
                description: api.description || api.Description,
              });

              const swaggerJson = APIProductService.getSwagger();
              if (swaggerJson) {
                setSwaggerSpec(swaggerJson);
                markDone();
                return;
              }
            }
          }
          markFailed(NOT_FOUND_MESSAGE);
        } catch (error) {
          console.error("Error fetching product by API endpoint alias:", error);
          markFailed(LOAD_FAILED_MESSAGE);
        }
      };

      fetchProductByAlias();
      return; // Exit early, don't run the legacy code below
    }

    // Legacy approach: If we have product identifier and API identifier, fetch the product
    if (productId && apiName) {
      const fetchProductWithService = async () => {
        markStarted();
        try {
          const userId = props.userId || "00000000-0000-0000-0000-000000000000";
          const csrfToken = props.csrfToken || "";
          const uuidRegex =
            /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

          let resolvedProductId = productId;

          // If route contains alias instead of UUID, resolve it first
          if (!uuidRegex.test(productId)) {
            let matchingProduct = findProductByAlias(
              APIProductService.getStoredProductList() || [],
              productId
            );

            if (!matchingProduct) {
              await APIProductService.getProductList(userId, csrfToken);
              matchingProduct = findProductByAlias(
                APIProductService.getStoredProductList() || [],
                productId
              );
            }

            // A current version under another stored alias (see findProductByFamilyAndVersion)
            if (!matchingProduct) {
              matchingProduct = findProductByFamilyAndVersion(
                APIProductService.getStoredProductList() || [],
                productId
              );
            }

            // Family URL fallback: not an exact alias for any version - try resolving
            // to the family's current latest non-deprecated version instead of failing.
            if (!matchingProduct) {
              matchingProduct = resolveLatestActiveVersionForFamily(
                APIProductService.getStoredProductList() || [],
                productId
              );
            }

            if (!matchingProduct) {
              console.error("Product not found for alias:", productId);
              markFailed(NOT_FOUND_MESSAGE);
              return;
            }

            resolvedProductId =
              matchingProduct.uuid || matchingProduct.id || matchingProduct.Id;
          }

          // Keep Redux in sync using UUID
          setProductUuid(resolvedProductId);
          fetchApiProduct({ apiProductId: resolvedProductId });

          // Fetch product document by UUID
          const productData = await APIProductService.getProductDocument(
            resolvedProductId,
            userId,
            csrfToken
          );

          if (productData) {
            // findAPI now supports both title and urlAlias
            const api = APIProductService.findAPI(apiName);

            if (api) {
              setApiEndpointData({
                title: api.apiName || api.ApiName,
                alias: api.urlAlias || api.UrlAlias || api.alias || null,
                swagger: api.swagger || api.Swagger,
                documentation: api.redocDocumentation || api.RedocDocumentation,
                version: api.apiVersion || api.ApiVersion,
                description: api.description || api.Description,
              });

              const swaggerJson = APIProductService.getSwagger();
              if (swaggerJson) {
                setSwaggerSpec(swaggerJson);
                markDone();
                return;
              }
            }
          }
          markFailed(NOT_FOUND_MESSAGE);
        } catch (error) {
          console.error(
            "Error fetching product with APIProductService:",
            error
          );
          markFailed(LOAD_FAILED_MESSAGE);
        }
      };

      fetchProductWithService();
    }
  }, [productId, apiName, useApiAlias, props.userId, props.csrfToken]);

  // When product is loaded, extract the API endpoint data
  useEffect(() => {
    if (currentProduct && currentProduct.apis && apiName && !swaggerSpec) {
      // Find the API endpoint in the product's APIs by name
      const decodedApiName = decodeURIComponent(apiName);
      const normalizedApiIdentifier = normalizeAlias(decodedApiName);

      const endpoint = currentProduct.apis.find((api) => {
        return (
          api.title === decodedApiName ||
          normalizeAlias(api.alias) === normalizedApiIdentifier
        );
      });

      if (endpoint) {
        setApiEndpointData(endpoint);

        // If we have swagger data, parse it to JSON
        if (endpoint.swagger) {
          const swaggerJson = parseSwaggerFromBase64(endpoint.swagger);
          if (swaggerJson) {
            setSwaggerSpec(swaggerJson);
            setLoadError(null);
          }
        }
      }
    }
  }, [currentProduct, apiName, swaggerSpec]);

  function getApiDocumentation(apiName) {
    if (apiName) {
      const decodedApiName = decodeURIComponent(apiName);
      const documentation = _.find(apiDocumentations, function (o) {
        return o.title === decodedApiName || o.apiName === decodedApiName;
      });
      if (documentation) {
        return documentation;
      }
    }
    return {
      title: null,
      botEnabled: null,
      botUrl: null,
      product: null,
    };
  }

  return (
    <div>
      <MetaData
        title={
          displayTitle ? displayTitle + " Documentation" : "Api Documentation"
        }
        url={
          FRONTEND_ORIGIN_URL_NO_SLASH +
          getRouteUrl(ROUTE_PRODUCT_DOCUMENTATION, {
            productId: match.params.productId,
            apiName: match.params.apiName,
          })
        }
      />

      {displayTitle && displayTitle !== "Get Token API" && crumbProduct && (
        <Breadcrumb
          {...breadcrumb}
          params={{
            product: {
              name: crumbProduct.title,
              href: crumbProductHref,
            },
            documentation: {
              name: "Documentation",
              href: crumbProductHref + "/documentation",
            },
            api: {
              name: displayTitle,
            },
          }}
          name="{api}"
          parents={["Products", "{product}", "{documentation}"]}
        />
      )}
      <SectionStart className="redoc_containerInner_noHeight">
        {swaggerSpec ? (
          <>
            <RedocTryoutHOC
              swaggerSpec={swaggerSpec}
              documentationId={apiName}
              productUuid={productUuid}
            />
            {botEnabled && <ChatBot botUrl={botUrl} />}

            {/* Changelog Section - same as ProductDetails component */}
            {parsedChangeLog && parsedChangeLog.length > 0 ? (
              <div className="changelog-container">
                <Heading
                  level="5"
                  style={{ color: "white", fontWeight: "400" }}
                >
                  ChangeLog
                </Heading>
                {_.orderBy(
                  parsedChangeLog,
                  [
                    (item) => {
                      // Parse version to handle numeric sorting properly
                      // e.g., "2.10" should come after "2.9"
                      // Numbers only ("v2.7 " -> "2.7"), so a prefix or stray space can't
                      // change the order.
                      const version = (item.version || "")
                        .toString()
                        .replace(/[^\d.]/g, "");
                      const parts = version.split(".");
                      // Pad each part to ensure proper sorting
                      return parts
                        .map((part) => part.padStart(10, "0"))
                        .join(".");
                    },
                  ],
                  ["desc"]
                ).map((item) => (
                  <ChangeLog key={v4()} {...item} />
                ))}
              </div>
            ) : null}
          </>
        ) : (
          !errorMessage && !loadError && <PagePreloader completed={false} />
        )}
        {(errorMessage || loadError) && (
          <ErrorMessage message={errorMessage || loadError} />
        )}
      </SectionStart>
    </div>
  );
}

// Memoize the parsed product to avoid re-parsing on every state change
let memoizedProduct = null;
let memoizedProductId = null;
let memoizedRawProduct = null;

const mapStateToProps = (state, ownProps) => {
  const productId = ownProps.match.params.productId;
  const rawProducts = state.apiProduct?.apiProductsById || {};

  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  let rawProduct = null;

  if (uuidRegex.test(productId)) {
    rawProduct = rawProducts[productId];
  } else {
    rawProduct =
      Object.values(rawProducts).find((product) => {
        const alias = product?.alias || product?.urlAlias || product?.UrlAlias;
        return normalizeAlias(alias) === normalizeAlias(productId);
      }) || null;
  }

  // Only re-parse if the product has actually changed
  let currentProduct = null;
  if (rawProduct) {
    if (memoizedProductId === productId && memoizedRawProduct === rawProduct) {
      // Use memoized version if product hasn't changed
      currentProduct = memoizedProduct;
    } else {
      // Parse the single product (parseApiProduct expects the full structure)
      currentProduct = parseApiProduct(rawProduct, state.apiDocumentation);
      // Update memoization
      memoizedProductId = productId;
      memoizedRawProduct = rawProduct;
      memoizedProduct = currentProduct;
    }
  }

  return {
    apiDocumentations: parseDocumentations(state.apiDocumentation.byId),
    loading: state.apiDocumentation.loading,
    errorMessage: state.apiDocumentation.errorMessage,
    currentProduct,
    rawProduct,
    userId: getUserIdFromState(state),
    csrfToken: state.applicationUser?.token,
  };
};

const mapDispatchToProps = (dispatch) => ({
  fetchApiDocumentationByAlias: (payload) => {
    dispatch(actions.fetchApiDocumentationByAlias(payload));
  },
  fetchApiDocumentation: (payload) => {
    dispatch(actions.fetchApiDocumentation(payload));
  },
  fetchApiProduct: (payload) => {
    dispatch(fetchApiProduct(payload));
  },
});

export default withTracker(
  AuthorizationHOC(ALL_USERS)(
    connect(mapStateToProps, mapDispatchToProps)(ApiDocumentationHOC)
  )
);
